import { T } from 'react-translator-component';

import { FEED_ISSUE_TEXTS, feedIssues } from './ocpiHelpers';
import StatusTag from './StatusTag';
import * as S from './styled';

import { CrossChargeBox } from '../../../types/responses/Ocpi';

interface Props {
  station: CrossChargeBox;
}

/**
 * Conteúdo do drawer da estação recebida: o que o parceiro vê dela no /locations (EVSE = conector)
 * e, quando o OCPI descarta, o motivo.
 */
const PartnerStationPanel = ({ station }: Props) => {
  const issues = feedIssues(station);
  const address = station.address;
  const hasCoordinates = station.location_latitude != null && station.location_longitude != null;
  const connectors = station.connectors || [];
  const street = address ? [address.street, address.house_number].filter(Boolean).join(', ') : '';

  return (
    <div>
      <S.DetailCard>
        <S.DetailTitle>{T('Feed')}</S.DetailTitle>
        <S.DetailRow>
          {T('Status')}
          <S.DetailValue>
            {issues.length ? <StatusTag label="Discarded" tone="err" /> : <StatusTag label="Goes into the feed" tone="ok" />}
          </S.DetailValue>
        </S.DetailRow>
        {issues.map((issue) => (
          <S.DetailRow key={issue}>
            {T('Reason')}
            <S.DetailValue>{T(FEED_ISSUE_TEXTS[issue])}</S.DetailValue>
          </S.DetailRow>
        ))}
        <S.DetailHint>
          {issues.length
            ? T('The OCPI service leaves this station out of the partner /locations until the data is fixed in the operator account.')
            : T('The partner receives this station in /locations. In OCPI, each connector is published as an EVSE.')}
        </S.DetailHint>
      </S.DetailCard>

      <S.DetailCard>
        <S.DetailTitle>{T('Station')}</S.DetailTitle>
        <S.DetailRow>
          {T('Operator')}
          <S.DetailValue>{station.tenant?.name || '—'}</S.DetailValue>
        </S.DetailRow>
        {station.description && (
          <S.DetailRow>
            {T('Description')}
            <S.DetailValue>{station.description}</S.DetailValue>
          </S.DetailRow>
        )}
        {station.online != null && (
          <S.DetailRow>
            {T('Connection')}
            <S.DetailValue>
              <StatusTag label={station.online ? 'Online' : 'Offline'} tone={station.online ? 'ok' : 'neutral'} />
            </S.DetailValue>
          </S.DetailRow>
        )}
        <S.DetailRow>
          {T('Station ID')}
          <S.DetailValue>
            <S.DetailMono>{station.charge_box_id}</S.DetailMono>
          </S.DetailValue>
        </S.DetailRow>
      </S.DetailCard>

      <S.DetailCard>
        <S.DetailTitle>{T('Location')}</S.DetailTitle>
        <S.DetailRow>
          {T('Address')}
          <S.DetailValue>{street || '—'}</S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          {T('City')}
          <S.DetailValue>{[address?.city, address?.state].filter(Boolean).join(' / ') || '—'}</S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          {T('Coordinates')}
          <S.DetailValue>
            {hasCoordinates ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${station.location_latitude},${station.location_longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`${T('Open in Google Maps')}`}
              >
                <S.DetailMono>
                  {station.location_latitude}, {station.location_longitude}
                </S.DetailMono>
              </a>
            ) : (
              '—'
            )}
          </S.DetailValue>
        </S.DetailRow>
      </S.DetailCard>

      <S.DetailCard>
        <S.DetailTitle>
          {T('Connectors')} ({connectors.length})
        </S.DetailTitle>
        {connectors.length === 0 && <S.DetailHint>{T('No connector registered.')}</S.DetailHint>}
        {connectors.map((c, i) => (
          <S.DetailRow key={c.connector_id || i}>
            {T('Connector')} {c.seq ?? c.connector_id ?? i + 1}
            <S.DetailValue>
              {[c.connector_type, c.power_max_value ? `${c.power_max_value} ${c.power_max_type || 'kW'}` : null, c.status]
                .filter(Boolean)
                .join(' · ') || '—'}
            </S.DetailValue>
          </S.DetailRow>
        ))}
      </S.DetailCard>
    </div>
  );
};

export default PartnerStationPanel;
